import React, { useState, useEffect } from "react";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";

// Components
import Layout from "../../../Layout/Layout";
import { InputField } from "../../../Components/formElements/InputField";
import { TextAreaField } from "../../../Components/formElements/TextAreaField";
import FileUploadComponentForHB from "../../../Components/formElements/FileUploadComponentForHB";
import FileViewerModalForHB from "../../../Components/FileViewer/FileViewerModalForHB";
import SuccessRegisterModal from "../../../Components/SuccessRegisterModal";
import FailureModal from "../../../Components/Modal/supplier_app/FailureModal";
import Loader from "../../../Components/Loader";
import Constants from "../../../common/SupplierConstants";
import ItemService from "../../../services/supplier_appCommonService";

// Redux
import { getSampleRequestDetail } from "../../../redux/feature/supplier_app/sampleSlice";

const SubmitArtwork = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  localStorage.setItem("menu", "Sampling Process");
  localStorage.setItem("submenu", "Sampling List");

  // Get sampleRequestNr and detail from Redux Store (with localStorage fallback on browser refresh)
  const sampleRequestNr = useSelector(
    (state) => state?.sample?.sample_request_detail?.sampleRequestNr,
  ) || localStorage.getItem("sample_request_nr");

  const sampleDetailData = useSelector(
    (state) => state?.sample?.sample_request_detail?.data,
  );
  const loading = useSelector(
    (state) => state?.sample?.sample_request_detail?.status || false,
  );

  const [disabled, setDisabled] = useState(false);

  // Accordion visibility toggles
  const [isCardVisibleSummary, setCardVisibleSummary] = useState(true);
  const [isCardVisibleArtwork, setCardVisibleArtwork] = useState(true);
  const [isCardVisibleMetadata, setCardVisibleMetadata] = useState(true);

  // Form Inputs
  const [formInputs, setFormInputs] = useState({
    brand: "",
    colour: "",
    sizes: [],
    additionalInfo: "",
    designFileAttachmentId: "",
    specSheetAttachmentId: "",
    referenceImageAttachmentId: "",
    brandingGuidelinesAttachmentId: "",
  });

  const standardSizes = ["XS", "S", "M", "L", "XL", "2XL", "3XL", "Free Size"];
  const [customSizeInput, setCustomSizeInput] = useState("");

  const handleToggleSize = (size) => {
    setFormInputs((prev) => {
      const currentSizes = Array.isArray(prev.sizes) ? prev.sizes : [];
      const exists = currentSizes.includes(size);
      return {
        ...prev,
        sizes: exists
          ? currentSizes.filter((s) => s !== size)
          : [...currentSizes, size],
      };
    });
  };

  const handleAddCustomSize = (e) => {
    if (e) e.preventDefault();
    const trimmed = customSizeInput.trim().toUpperCase();
    if (!trimmed) return;
    setFormInputs((prev) => {
      const currentSizes = Array.isArray(prev.sizes) ? prev.sizes : [];
      if (!currentSizes.includes(trimmed)) {
        return { ...prev, sizes: [...currentSizes, trimmed] };
      }
      return prev;
    });
    setCustomSizeInput("");
  };

  const handleSelectAllSizes = () => {
    setFormInputs((prev) => ({
      ...prev,
      sizes: [...standardSizes],
    }));
  };

  const handleClearSizes = () => {
    setFormInputs((prev) => ({
      ...prev,
      sizes: [],
    }));
  };

  // File Viewer modal state
  const [getShowFileViewModal, setShowFileViewModal] = useState(false);
  const [getFileViewData, setFileViewData] = useState(null);

  const handleViewFile = (fileAttachmentId) => {
    setFileViewData(fileAttachmentId);
    setShowFileViewModal(true);
  };

  const handleViewFileHide = () => {
    setShowFileViewModal(false);
  };

  // Modals
  const [getShowSuccessModal, setShowSuccessModal] = useState(false);
  const [getModalInfo, setModalInfo] = useState({});
  const [getShowFailureModal, setShowFailureModal] = useState(false);
  const [getFailureModal, setFailureModal] = useState({});
  const [getErrorFields, setErrorFields] = useState([]);

  // Fetch sample detail on mount
  useEffect(() => {
    if (sampleRequestNr) {
      dispatch(getSampleRequestDetail({ sampleRequestNr }));
    }
  }, [dispatch, sampleRequestNr]);

  // Sync Redux detail into form inputs
  useEffect(() => {
    if (sampleDetailData && Object.keys(sampleDetailData).length > 0) {
      const rawSizes = sampleDetailData?.artwork?.sizes || sampleDetailData?.sizes;
      const initialSizes = Array.isArray(rawSizes)
        ? rawSizes
        : typeof rawSizes === "string" && rawSizes.trim()
        ? rawSizes.split(",").map((s) => s.trim()).filter(Boolean)
        : [];

      setFormInputs({
        brand: sampleDetailData?.artwork?.brand || sampleDetailData?.brand || "",
        colour: sampleDetailData?.artwork?.colour || sampleDetailData?.colour || "",
        sizes: initialSizes,
        additionalInfo:
          sampleDetailData?.artwork?.additionalInfo ||
          sampleDetailData?.additionalInfo ||
          "",
        designFileAttachmentId:
          sampleDetailData?.artwork?.files?.designFileAttachmentId ||
          sampleDetailData?.designFileAttachmentId ||
          sampleDetailData?.files?.designFileAttachmentId ||
          "",
        specSheetAttachmentId:
          sampleDetailData?.artwork?.files?.specSheetAttachmentId ||
          sampleDetailData?.specSheetAttachmentId ||
          sampleDetailData?.files?.specSheetAttachmentId ||
          "",
        referenceImageAttachmentId:
          sampleDetailData?.artwork?.files?.referenceImageAttachmentId ||
          sampleDetailData?.referenceImageAttachmentId ||
          sampleDetailData?.files?.referenceImageAttachmentId ||
          "",
        brandingGuidelinesAttachmentId:
          sampleDetailData?.artwork?.files?.brandingGuidelinesAttachmentId ||
          sampleDetailData?.brandingGuidelinesAttachmentId ||
          sampleDetailData?.files?.brandingGuidelinesAttachmentId ||
          "",
      });
    }
  }, [sampleDetailData]);

  const onInputChange = ({ target: { name, value } }) => {
    setFormInputs((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errors = [];
    if (!formInputs.brand?.trim()) errors.push("Brand");
    if (!formInputs.colour?.trim()) errors.push("Colour / Shade");
    if (!formInputs.sizes || formInputs.sizes.length === 0) errors.push("Sizes");

    if (errors.length > 0) {
      setErrorFields(errors);
      setFailureModal({
        title: "Validation Error",
        msg: "Please fill all mandatory branding specifications before submitting.",
      });
      setShowFailureModal(true);
      return;
    }

    const toastId = toast.loading("Submitting Artwork...", { position: "top-right" });
    setDisabled(true);

    try {
      const parsedSizes = Array.isArray(formInputs.sizes)
        ? formInputs.sizes
        : typeof formInputs.sizes === "string"
        ? formInputs.sizes.split(",").map((s) => s.trim()).filter(Boolean)
        : [];

      const payload = {
        sampleRequestNr: sampleRequestNr,
        brand: formInputs.brand,
        colour: formInputs.colour,
        sizes: parsedSizes,
        additionalInfo: formInputs.additionalInfo,
        status: "Under Review",
        currentStep: 3,
        designFileAttachmentId: formInputs.designFileAttachmentId,
        specSheetAttachmentId: formInputs.specSheetAttachmentId,
        referenceImageAttachmentId: formInputs.referenceImageAttachmentId,
        brandingGuidelinesAttachmentId: formInputs.brandingGuidelinesAttachmentId,
      };

      const res = await ItemService.updateSampleArtwork(payload);
      toast.dismiss(toastId);

      if (res?.status === 200) {
        setModalInfo({
          title: "Artwork Submitted Successfully",
          successMsg:
            res?.message ||
            `Artwork and specifications for Sample Request ${sampleRequestNr} have been submitted for Approval.`,
        });
        setShowSuccessModal(true);
      } else {
        toast.error(res?.message || "Failed to update artwork");
      }
    } catch (err) {
      toast.dismiss(toastId);
      console.error("Error submitting artwork:", err);
      toast.error("Error submitting artwork to backend");
    }

    setDisabled(false);
  };

  const hideSuccessModal = () => {
    setShowSuccessModal(false);
    navigate(Constants.SAMPLE_REQUEST_LIST);
  };

  return (
    <main className="dashboard main" id="main">
      <Layout />
      <Row className="justify-center subContent">
        <Col xs={12} md={12} className="rightSide quoation">
          <div className="rightSideInner PageCommonCSS">
            <h2 className="mainTitle">Submit Artwork & Specifications</h2>
            <p className="subText">Sample Request Details: <strong>{sampleRequestNr}</strong></p>
            <hr />

            <Loader isLoading={loading} className={"text-center"} />

            {!loading && (
              <>
                {/* SECTION 1: BU Sample Request Summary (Read-Only) */}
                <div className="accordion mb-4" id="buSummaryAccordion">
                  <div className="accordion-item">
                    <h2 className="accordion-header">
                      <Row
                        className="accordion-button"
                        onClick={() => setCardVisibleSummary(!isCardVisibleSummary)}
                      >
                        <Col xs={1} md={1} lg={1} className="text-left arrowBtn">
                          <i
                            className={`bi ${
                              isCardVisibleSummary ? "bi-chevron-right" : "bi-chevron-down"
                            }`}
                          ></i>
                        </Col>
                        <Col xs={1} md={1} lg={1} className="text-center logoBtn">
                          <span className="iconLogo">BU</span>
                        </Col>
                        <Col xs={12} md={9} lg={9}>
                          <span className="headerTitle">BU Request Details</span>
                          <span className="subHeaderTitle">
                            Base requirements submitted by Business Unit (Read-Only)
                          </span>
                        </Col>
                      </Row>
                    </h2>

                    <div
                      className={`accordion-collapse collapse show ${
                        isCardVisibleSummary ? "visible" : ""
                      }`}
                    >
                      <div className="accordion-body">
                        <Row className="pt-2 pb-4">
                          <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
                            <InputField
                              className="inputBox"
                              label="Sample Request Number"
                              value={sampleRequestNr || ""}
                              disabled={true}
                            />
                          </Col>

                          <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
                            <InputField
                              className="inputBox"
                              label="Business Unit"
                              value={sampleDetailData?.businessUnit || "Apparel"}
                              disabled={true}
                            />
                          </Col>

                          <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
                            <InputField
                              className="inputBox"
                              label="Item Category"
                              value={sampleDetailData?.itemCategory || "Men's Wear"}
                              disabled={true}
                            />
                          </Col>

                          <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
                            <InputField
                              className="inputBox"
                              label="Item Description"
                              value={sampleDetailData?.itemDescription || "Cotton Polo T-Shirt"}
                              disabled={true}
                            />
                          </Col>

                          <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
                            <InputField
                              className="inputBox"
                              label="Expected Sample Date"
                              value={sampleDetailData?.expectedSampleDate || ""}
                              disabled={true}
                            />
                          </Col>

                          <Col xs={12} md={4} lg={4} className="commTopButtonRightLeftPadding">
                            <InputField
                              className="inputBox"
                              label="Sample Quantity"
                              value={`${sampleDetailData?.sampleQuantity || 2} ${sampleDetailData?.sampleUom || "Nos"}`}
                              disabled={true}
                            />
                          </Col>

                          <Col xs={12} md={12} lg={12} className="commTopButtonRightLeftPadding">
                            <TextAreaField
                              className="inputBox"
                              label="BU Remarks"
                              value={sampleDetailData?.remarks || ""}
                              disabled={true}
                              rows={2}
                            />
                          </Col>
                        </Row>
                      </div>
                    </div>
                  </div>
                </div>

                {/* SECTION 2: Artwork & Documentation Uploads */}
                <div className="accordion mb-4" id="artworkUploadAccordion">
                  <div className="accordion-item">
                    <h2 className="accordion-header">
                      <Row
                        className="accordion-button"
                        onClick={() => setCardVisibleArtwork(!isCardVisibleArtwork)}
                      >
                        <Col xs={1} md={1} lg={1} className="text-left arrowBtn">
                          <i
                            className={`bi ${
                              isCardVisibleArtwork ? "bi-chevron-right" : "bi-chevron-down"
                            }`}
                          ></i>
                        </Col>
                        <Col xs={1} md={1} lg={1} className="text-center logoBtn">
                          <span className="iconLogo">AW</span>
                        </Col>
                        <Col xs={12} md={9} lg={9}>
                          <span className="headerTitle">Artwork & Specification Files</span>
                          <span className="subHeaderTitle">
                            Upload design files, specification sheets, mockups, and branding guidelines
                          </span>
                        </Col>
                      </Row>
                    </h2>

                    <div
                      className={`accordion-collapse collapse show ${
                        isCardVisibleArtwork ? "visible" : ""
                      }`}
                    >
                      <div className="accordion-body">
                        <Row className="pt-2 pb-3">
                          {/* 1. Design File */}
                          <Col xs={12} md={6} lg={6} className="commTopButtonRightLeftPadding mb-4">
                            <div className="p-3 border rounded bg-white shadow-sm" style={{ borderColor: "#dee2e6" }}>
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <label className="block text-sm font-semibold text-gray-900 m-0">
                                  Design File (Vector Artwork) <span style={{ color: "red" }}>*</span>
                                </label>
                                {formInputs.designFileAttachmentId ? (
                                  <span
                                    className="badge bg-success-subtle text-success border border-success d-inline-flex align-items-center gap-1"
                                    style={{ cursor: "pointer", padding: "5px 10px", fontSize: "12px", borderRadius: "12px" }}
                                    onClick={() => handleViewFile(formInputs.designFileAttachmentId)}
                                    title="Click to view file"
                                  >
                                    <i className="bi bi-paperclip"></i> View File
                                  </span>
                                ) : (
                                  <span
                                    className="badge bg-light text-secondary border"
                                    style={{ fontSize: "11px", borderRadius: "12px", padding: "4px 8px" }}
                                  >
                                    Not Uploaded
                                  </span>
                                )}
                              </div>

                              <FileUploadComponentForHB
                                label=""
                                name="designFileAttachmentId"
                                allowedTypes={["image/png", "image/jpeg", "application/pdf", ".ai", ".psd", ".cdr"]}
                                multiple={false}
                                value={formInputs.designFileAttachmentId || ""}
                                onSuccessUpload={(payload) => {
                                  setFormInputs((prev) => ({
                                    ...prev,
                                    designFileAttachmentId: payload.attachmentId,
                                  }));
                                }}
                              />

                              <div className="text-muted small mt-2 d-flex align-items-center" style={{ fontSize: "11px" }}>
                                <i className="bi bi-info-circle me-1 text-primary"></i>
                                Supported Formats: .AI, .PSD, .CDR, .PDF, .PNG
                              </div>
                            </div>
                          </Col>

                          {/* 2. Specification Sheet */}
                          <Col xs={12} md={6} lg={6} className="commTopButtonRightLeftPadding mb-4">
                            <div className="p-3 border rounded bg-white shadow-sm" style={{ borderColor: "#dee2e6" }}>
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <label className="block text-sm font-semibold text-gray-900 m-0">
                                  Specification Sheet / Tech Pack <span style={{ color: "red" }}>*</span>
                                </label>
                                {formInputs.specSheetAttachmentId ? (
                                  <span
                                    className="badge bg-success-subtle text-success border border-success d-inline-flex align-items-center gap-1"
                                    style={{ cursor: "pointer", padding: "5px 10px", fontSize: "12px", borderRadius: "12px" }}
                                    onClick={() => handleViewFile(formInputs.specSheetAttachmentId)}
                                    title="Click to view file"
                                  >
                                    <i className="bi bi-paperclip"></i> View File
                                  </span>
                                ) : (
                                  <span
                                    className="badge bg-light text-secondary border"
                                    style={{ fontSize: "11px", borderRadius: "12px", padding: "4px 8px" }}
                                  >
                                    Not Uploaded
                                  </span>
                                )}
                              </div>

                              <FileUploadComponentForHB
                                label=""
                                name="specSheetAttachmentId"
                                allowedTypes={["application/pdf", ".docx", ".xlsx", ".doc"]}
                                multiple={false}
                                value={formInputs.specSheetAttachmentId || ""}
                                onSuccessUpload={(payload) => {
                                  setFormInputs((prev) => ({
                                    ...prev,
                                    specSheetAttachmentId: payload.attachmentId,
                                  }));
                                }}
                              />

                              <div className="text-muted small mt-2 d-flex align-items-center" style={{ fontSize: "11px" }}>
                                <i className="bi bi-info-circle me-1 text-primary"></i>
                                Supported Formats: .DOCX, .XLSX, .PDF
                              </div>
                            </div>
                          </Col>

                          {/* 3. Reference Image */}
                          <Col xs={12} md={6} lg={6} className="commTopButtonRightLeftPadding mb-4">
                            <div className="p-3 border rounded bg-white shadow-sm" style={{ borderColor: "#dee2e6" }}>
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <label className="block text-sm font-semibold text-gray-900 m-0">
                                  Reference Image / Mockup
                                </label>
                                {formInputs.referenceImageAttachmentId ? (
                                  <span
                                    className="badge bg-success-subtle text-success border border-success d-inline-flex align-items-center gap-1"
                                    style={{ cursor: "pointer", padding: "5px 10px", fontSize: "12px", borderRadius: "12px" }}
                                    onClick={() => handleViewFile(formInputs.referenceImageAttachmentId)}
                                    title="Click to view file"
                                  >
                                    <i className="bi bi-paperclip"></i> View File
                                  </span>
                                ) : (
                                  <span
                                    className="badge bg-light text-secondary border"
                                    style={{ fontSize: "11px", borderRadius: "12px", padding: "4px 8px" }}
                                  >
                                    Not Uploaded
                                  </span>
                                )}
                              </div>

                              <FileUploadComponentForHB
                                label=""
                                name="referenceImageAttachmentId"
                                allowedTypes={["image/png", "image/jpeg", "image/webp"]}
                                multiple={false}
                                value={formInputs.referenceImageAttachmentId || ""}
                                onSuccessUpload={(payload) => {
                                  setFormInputs((prev) => ({
                                    ...prev,
                                    referenceImageAttachmentId: payload.attachmentId,
                                  }));
                                }}
                              />

                              <div className="text-muted small mt-2 d-flex align-items-center" style={{ fontSize: "11px" }}>
                                <i className="bi bi-info-circle me-1 text-primary"></i>
                                Supported Formats: .JPG, .PNG, .WEBP
                              </div>
                            </div>
                          </Col>

                          {/* 4. Branding Guidelines */}
                          <Col xs={12} md={6} lg={6} className="commTopButtonRightLeftPadding mb-4">
                            <div className="p-3 border rounded bg-white shadow-sm" style={{ borderColor: "#dee2e6" }}>
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <label className="block text-sm font-semibold text-gray-900 m-0">
                                  Branding Guidelines & Logo Standards
                                </label>
                                {formInputs.brandingGuidelinesAttachmentId ? (
                                  <span
                                    className="badge bg-success-subtle text-success border border-success d-inline-flex align-items-center gap-1"
                                    style={{ cursor: "pointer", padding: "5px 10px", fontSize: "12px", borderRadius: "12px" }}
                                    onClick={() => handleViewFile(formInputs.brandingGuidelinesAttachmentId)}
                                    title="Click to view file"
                                  >
                                    <i className="bi bi-paperclip"></i> View File
                                  </span>
                                ) : (
                                  <span
                                    className="badge bg-light text-secondary border"
                                    style={{ fontSize: "11px", borderRadius: "12px", padding: "4px 8px" }}
                                  >
                                    Not Uploaded
                                  </span>
                                )}
                              </div>

                              <FileUploadComponentForHB
                                label=""
                                name="brandingGuidelinesAttachmentId"
                                allowedTypes={["application/pdf"]}
                                multiple={false}
                                value={formInputs.brandingGuidelinesAttachmentId || ""}
                                onSuccessUpload={(payload) => {
                                  setFormInputs((prev) => ({
                                    ...prev,
                                    brandingGuidelinesAttachmentId: payload.attachmentId,
                                  }));
                                }}
                              />

                              <div className="text-muted small mt-2 d-flex align-items-center" style={{ fontSize: "11px" }}>
                                <i className="bi bi-info-circle me-1 text-primary"></i>
                                Supported Formats: .PDF
                              </div>
                            </div>
                          </Col>
                        </Row>
                      </div>
                    </div>
                  </div>
                </div>

                {/* SECTION 3: Product Branding & Metadata */}
                <div className="accordion mb-4" id="brandingMetadataAccordion">
                  <div className="accordion-item">
                    <h2 className="accordion-header">
                      <Row
                        className="accordion-button"
                        onClick={() => setCardVisibleMetadata(!isCardVisibleMetadata)}
                      >
                        <Col xs={1} md={1} lg={1} className="text-left arrowBtn">
                          <i
                            className={`bi ${
                              isCardVisibleMetadata ? "bi-chevron-right" : "bi-chevron-down"
                            }`}
                          ></i>
                        </Col>
                        <Col xs={1} md={1} lg={1} className="text-center logoBtn">
                          <span className="iconLogo">SP</span>
                        </Col>
                        <Col xs={12} md={9} lg={9}>
                          <span className="headerTitle">Product Branding & Metadata</span>
                          <span className="subHeaderTitle">
                            Define brand name, color shade, sizes, and logo placement
                          </span>
                        </Col>
                      </Row>
                    </h2>

                    <div
                      className={`accordion-collapse collapse show ${
                        isCardVisibleMetadata ? "visible" : ""
                      }`}
                    >
                      <div className="accordion-body">
                        <Row className="pt-2 pb-4">
                          {/* Brand */}
                          <Col xs={12} md={6} lg={6} className="commTopButtonRightLeftPadding mb-3">
                            <InputField
                              id="brand"
                              className="inputBox"
                              label="Brand"
                              name="brand"
                              placeholder="e.g. Ramraj"
                              value={formInputs.brand}
                              onChange={onInputChange}
                              required={true}
                            />
                          </Col>

                          {/* Colour / Shade */}
                          <Col xs={12} md={6} lg={6} className="commTopButtonRightLeftPadding mb-3">
                            <InputField
                              id="colour"
                              className="inputBox"
                              label="Colour / Shade"
                              name="colour"
                              placeholder="e.g. Bottle Green"
                              value={formInputs.colour}
                              onChange={onInputChange}
                              required={true}
                            />
                          </Col>

                          {/* Sizes - Interactive Chip Selector (NO comma separated text input) */}
                          <Col xs={12} md={12} lg={12} className="commTopButtonRightLeftPadding mb-3">
                            <div className="p-3 rounded bg-light border">
                              <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                                <label className="block text-sm font-semibold text-gray-900 m-0">
                                  Applicable Sizes <span style={{ color: "red" }}>*</span>
                                </label>
                                <div className="d-flex align-items-center gap-2">
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary"
                                    style={{ fontSize: "11px", padding: "2px 8px" }}
                                    onClick={handleSelectAllSizes}
                                  >
                                    Select All
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-danger"
                                    style={{ fontSize: "11px", padding: "2px 8px" }}
                                    onClick={handleClearSizes}
                                  >
                                    Clear
                                  </button>
                                </div>
                              </div>

                              {/* Standard Size Buttons */}
                              <div className="d-flex flex-wrap gap-2 mb-2">
                                {standardSizes.map((size) => {
                                  const isSelected = formInputs.sizes.includes(size);
                                  return (
                                    <button
                                      key={size}
                                      type="button"
                                      onClick={() => handleToggleSize(size)}
                                      className="btn"
                                      style={{
                                        minWidth: "52px",
                                        height: "36px",
                                        borderRadius: "6px",
                                        fontWeight: 600,
                                        fontSize: "13px",
                                        transition: "all 0.15s ease-in-out",
                                        backgroundColor: isSelected ? "#0d6efd" : "#ffffff",
                                        color: isSelected ? "#ffffff" : "#495057",
                                        border: isSelected ? "1px solid #0d6efd" : "1px solid #ced4da",
                                        boxShadow: isSelected ? "0 2px 4px rgba(13,110,253,0.25)" : "none",
                                      }}
                                    >
                                      {isSelected && <i className="bi bi-check me-1"></i>}
                                      {size}
                                    </button>
                                  );
                                })}
                              </div>

                              {/* Custom Size Addition & Summary */}
                              <div className="d-flex flex-wrap align-items-center gap-2 mt-2 pt-2 border-top">
                                <span className="text-muted small" style={{ fontSize: "12px" }}>Custom Size:</span>
                                <input
                                  type="text"
                                  placeholder="e.g. 38, 40, Free"
                                  value={customSizeInput}
                                  onChange={(e) => setCustomSizeInput(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                      e.preventDefault();
                                      handleAddCustomSize(e);
                                    }
                                  }}
                                  className="form-control form-control-sm"
                                  style={{ width: "130px", height: "30px", fontSize: "12px" }}
                                />
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-primary"
                                  style={{ fontSize: "11px", height: "30px", padding: "0 10px" }}
                                  onClick={handleAddCustomSize}
                                >
                                  <i className="bi bi-plus-lg"></i> Add
                                </button>

                                {/* Selected Size Summary Chips */}
                                {formInputs.sizes.length > 0 && (
                                  <div className="d-flex flex-wrap align-items-center gap-1 ms-sm-3 mt-2 mt-sm-0">
                                    <span className="text-muted small me-1" style={{ fontSize: "11px", fontWeight: 600 }}>
                                      Selected ({formInputs.sizes.length}):
                                    </span>
                                    {formInputs.sizes.map((size) => (
                                      <span
                                        key={size}
                                        className="badge bg-primary d-inline-flex align-items-center gap-1"
                                        style={{ fontSize: "11px", padding: "5px 8px", borderRadius: "10px" }}
                                      >
                                        {size}
                                        <i
                                          className="bi bi-x"
                                          style={{ cursor: "pointer", fontSize: "13px" }}
                                          onClick={() => handleToggleSize(size)}
                                          title={`Remove ${size}`}
                                        ></i>
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          </Col>

                          {/* Additional Information */}
                          <Col xs={12} md={12} lg={12} className="commTopButtonRightLeftPadding">
                            <TextAreaField
                              id="additionalInfo"
                              className="inputBox"
                              label="Additional Information / Logo Placement"
                              name="additionalInfo"
                              placeholder="e.g. Use Ramraj logo on left chest"
                              value={formInputs.additionalInfo}
                              onChange={onInputChange}
                              rows={3}
                            />
                          </Col>
                        </Row>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Page Footer Buttons */}
                <div className="pageFooter mb-4 pb-2">
                  <Row className="rowDirection">
                    <Col xs={12} md={3} lg={3} className="">
                      <button
                        className="btnTable"
                        onClick={handleSubmit}
                        disabled={disabled}
                      >
                        <i className="bi bi-save"></i> {disabled ? "Submitting..." : "Submit Artwork"}
                      </button>
                    </Col>
                    <Col xs={12} md={2} lg={2} className="">
                      <button
                        className="btnTable"
                        onClick={() => navigate(Constants.SAMPLE_REQUEST_LIST)}
                      >
                        <i className="bi bi-x-circle"></i> Cancel
                      </button>
                    </Col>
                  </Row>
                </div>
              </>
            )}
          </div>
        </Col>
      </Row>

      {/* File Viewer Modal for Attached Documents */}
      <FileViewerModalForHB
        show={getShowFileViewModal}
        onHide={handleViewFileHide}
        fileid={getFileViewData}
      />

      {/* Success Modal */}
      <SuccessRegisterModal
        show={getShowSuccessModal}
        onHide={() => hideSuccessModal()}
        modaldata={getModalInfo}
        aftersuccess={Constants.SAMPLE_REQUEST_LIST}
      />

      {/* Failure Modal */}
      <FailureModal
        show={getShowFailureModal}
        onHide={() => setShowFailureModal(false)}
        failuremodaldata={getFailureModal}
        requiredfields={getErrorFields}
      />
    </main>
  );
};

export default SubmitArtwork;
